import isEqual from 'lodash/isEqual';
import { useReducer } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isArray from 'lodash/isArray';
import ENDPOINT from '../constants/endpoints';
import useApi from './useApi';

const TYPE = {
  INIT: 'INIT',
  CLEAR: 'CLEAR',
  CHANGE: 'CHANGE',
  PUSH: 'PUSH',
  GROUP_SELECT: 'GROUP_SELECT',
  GROUP_CLEAR: 'GROUP_CLEAR',
};

const getFiltersQueryParams = sections => {
  const filters = {};
  Object.keys(sections).forEach(section => {
    const { groups } = sections[section];

    Object.keys(groups).forEach(group => {
      const { name, elements = [], type = null } = groups[group];
      const groupName = name.replace(/\[]$/, '');

      if (groupName === 'graduation') {
        if (groups[group].props && groups[group].props.clearValue) {
          if (!isEqual(groups[group].selectedElements, groups[group].props.clearValue)) {
            filters[groupName] = groups[group].selectedElements;
          }
        }
      } else if (type === 'datePicker') {
        filters[groupName] = groups[group].selectedElements;
      } else {
        if (!filters[groupName]) {
          filters[groupName] = [];
        }

        elements.forEach(element =>
          element.selected ? filters[groupName].push(element.value) : null
        );
      }
    });
  });

  const params = cloneDeep(filters);
  for (let field in params) {
    if (isArray(params[field]) && !params[field].length) {
      delete params[field];
    }
  }

  return params;
};

const initSelectFilters = sections => {
  const filters = {};

  Object.keys(sections).forEach(section => {
    const {
      [section]: { groups },
    } = sections;

    Object.keys(groups).forEach(group => {
      const {
        [group]: { elements = [], selectedElements = [], limit = null },
      } = groups;

      if (limit) {
        groups[group].type = 'searchable';
      }

      if (selectedElements.length > 0) {
        elements.forEach(elem => {
          if (
            selectedElements.indexOf(elem.value) !== -1 ||
            selectedElements.indexOf(Number(elem.value)) !== -1
          ) {
            elem.selected = true;
          }
        });
      }
    });

    filters[section] = { ...sections[section] };
  });

  const params = cloneDeep(filters);
  for (let field in params) {
    if (isArray(params[field]) && !params[field].length) {
      delete params[field];
    }
  }

  return params;
};

const reducer = (state, action) => {
  switch (action.type) {
    case TYPE.INIT: {
      const { data } = action;
      const sections = initSelectFilters(data);

      return {
        sections: { ...sections },
        query: getFiltersQueryParams(sections),
      };
    }
    case TYPE.CLEAR: {
      const { sections } = state;
      Object.keys(sections).forEach(sectionKey => {
        const { groups } = sections[sectionKey];

        Object.keys(groups).forEach(groupKey => {
          const { elements, type } = groups[groupKey];

          if (groupKey === 'graduation') {
            if (groups[groupKey].props && groups[groupKey].props.clearValue) {
              groups[groupKey].selectedElements = groups[groupKey].props.clearValue;
            } else {
              groups[groupKey].selectedElements = null;
            }
          } else if (type === 'datePicker' || type === 'range') {
            groups[groupKey].selectedElements = null;
          } else {
            elements.forEach(element => (element.selected = false));
          }
        });
      });

      return {
        sections: { ...sections },
        query: getFiltersQueryParams(sections),
      };
    }
    case TYPE.CHANGE: {
      const { section, group, value } = action.data;
      const { sections } = state;
      const {
        [section]: {
          groups: {
            [group]: { elements, type = null },
          },
        },
      } = sections;

      if (type === 'datePicker' || type === 'range') {
        sections[section].groups[group].selectedElements = value;
      } else if (type === 'scores') {
        const exceptFirst = elements[0].text === 'NA';
        if (elements[0].value === value && exceptFirst) {
          elements[0].selected = !elements[0].selected;
        } else {
          let above = true;
          elements.forEach((element, index) => {
            if (element.value === value) {
              above = false;
              element.selected = !element.selected;
            } else if (!exceptFirst || (exceptFirst && index !== 0)) {
              element.selected = above;
            }
          });
        }
      } else {
        elements.some(element => {
          const selectedElement = element.value === value;
          if (selectedElement) {
            element.selected = !element.selected;
          }
          return selectedElement;
        });
      }

      return {
        sections: { ...sections },
        query: getFiltersQueryParams(sections),
      };
    }
    case TYPE.PUSH: {
      const { section, group, value } = action.data;
      const { sections } = state;
      const {
        [section]: {
          groups: {
            [group]: { elements },
          },
        },
      } = sections;

      elements.push({
        ...value,
        selected: true,
      });

      return {
        sections: { ...sections },
        query: getFiltersQueryParams(sections),
      };
    }
    case TYPE.GROUP_SELECT: {
      const { sections } = state;
      const { section, group } = action.data;
      const {
        [section]: {
          groups: {
            [group]: { elements },
          },
        },
      } = sections;
      elements.forEach(element => (element.selected = true));

      return {
        sections: { ...sections },
        query: getFiltersQueryParams(sections),
      };
    }
    case TYPE.GROUP_CLEAR: {
      const { sections } = state;
      const { section, group } = action.data;
      const {
        [section]: {
          groups: {
            [group]: { elements },
          },
        },
      } = sections;
      elements.forEach(element => (element.selected = false));

      return {
        sections: { ...sections },
        query: getFiltersQueryParams(sections),
      };
    }
    default:
      throw new Error();
  }
};

/**
 * @deprecated
 * @see useGridWithFilters
 */
export const useFiltersAccordion = filters => {
  const api = useApi();

  const [state, dispatch] = useReducer(reducer, {
    sections: {},
    query: null,
  });

  const init = filters => {
    dispatch({ type: TYPE.INIT, data: { ...filters } });
  };

  const clear = () => {
    dispatch({ type: TYPE.CLEAR });
  };

  const change = (section, group, value) => {
    dispatch({ type: TYPE.CHANGE, data: { section, group, value } });
  };

  const groupClear = (section, group) => {
    dispatch({ type: TYPE.GROUP_CLEAR, data: { section, group } });
  };

  const push = (section, group, value) => {
    dispatch({ type: TYPE.PUSH, data: { section, group, value } });
  };

  const search = (value, group) =>
    new Promise(resolve => {
      api.get(
        ENDPOINT.FILTERS.FIND,
        { value, group },
        {
          handleError: response =>
            dispatch({ type: TYPE.FAIL, error: response }),
          handleSuccess: ({ rows = [] }) => {
            resolve(rows.map(item => ({ ...item, label: item.text })));
          },
        }
      );
    });

  const groupSelect = (section, group) => {
    dispatch({ type: TYPE.GROUP_SELECT, data: { section, group } });
  };

  return [
    state,
    { init, clear, change, push, search, groupClear, groupSelect },
  ];
};
